import type { ScoreTrace } from '@cyberscore/contracts';
import {
  Briefcase,
  Building2,
  ClipboardList,
  DatabaseBackup,
  Droplets,
  Eye,
  Factory,
  FileLock2,
  Globe,
  GraduationCap,
  HeartPulse,
  KeyRound,
  Landmark,
  Laptop,
  Mail,
  ShieldCheck,
  ShoppingCart,
  Siren,
  Truck,
  Package,
  RadioTower,
  Scale,
  Target,
  Zap,
  type LucideIcon,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { href } from '../../lib/router';
import type { AssessmentSummary } from '../assessments/api';

/** Small pieces the business pages share. */

const MISSION_ICONS: Record<string, LucideIcon> = {
  accounts: KeyRound,
  email: Mail,
  ransomware: DatabaseBackup,
  internet: Globe,
  devices: Laptop,
  data: FileLock2,
  watch: Eye,
  ready: Siren,
  suppliers: Truck,
  governance: ClipboardList,
  factory: Factory,
  patients: HeartPulse,
  checkout: ShoppingCart,
  fleet: Package,
  regulator: Landmark,
  rmit: ShieldCheck,
  csa: Scale,
  clients: Briefcase,
  plant: Zap,
  supply: Droplets,
  network: RadioTower,
  public: Building2,
  campus: GraduationCap,
};

export function MissionIcon({ id, size = 18 }: { id: string; size?: number }) {
  const I = MISSION_ICONS[id] ?? Target;
  return <I size={size} />;
}

/** A tinted square holding an icon, as the prototype's card heads have. */
export function HIcon({ children, tone = '', big = false }: { children: ReactNode; tone?: '' | 'ok' | 'warn' | 'bad' | 'acc'; big?: boolean }) {
  return <span className={`hicon ${tone}${big ? ' big' : ''}`}>{children}</span>;
} // prettier-ignore

export function CardHead({ icon, title, sub, end, tone = '' }: { icon: ReactNode; title: ReactNode; sub?: ReactNode; end?: ReactNode; tone?: '' | 'ok' | 'warn' | 'bad' | 'acc' }) {
  return (
    <div className="bcard-head">
      <HIcon tone={tone}>{icon}</HIcon>
      <div>
        <h3>{title}</h3>
        {sub && <div className="sub">{sub}</div>}
      </div>
      {end && <div className="end">{end}</div>}
    </div>
  );
} // prettier-ignore

/** A score's points as a chip: green up, red down, a dash for none. */
export function PtsChip({ n }: { n: number }) {
  return <span className={`pts-chip ${n > 0 ? 'up' : n < 0 ? 'down' : 'zero'}`}>{n > 0 ? `+${n}` : n < 0 ? `−${-n}` : '—'}</span>;
} // prettier-ignore

/**
 * Where to act on a control: its question while the assessment takes answers, else its mission. The label
 * says which.
 */
export function controlLink(a: AssessmentSummary, trace: ScoreTrace, control: string, mission: string) {
  const c = trace.controls.find((x) => x.control === control);
  const q = c?.questions[0];
  if (a.status === 'in_progress' && q)
    return { to: href.answer(a.id, q), label: c.status === 'unanswered' ? 'Answer' : 'Update' };
  return { to: href.missions(mission), label: 'See mission' };
}

export const shortDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-MY', { day: 'numeric', month: 'short' });
